import * as api from '../api';

/**
 * Web Push subscription flow for this device.
 *
 * Push needs three things the browser gates behind user permission: a registered service
 * worker, notification permission, and a PushSubscription tied to the server's VAPID public
 * key. This wraps all three so the Settings toggle is a single call.
 */

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let swReg: ServiceWorkerRegistration | null = null;

async function register(): Promise<ServiceWorkerRegistration> {
  if (swReg) return swReg;
  swReg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return swReg;
}

/** VAPID public keys are base64url; PushManager wants an ArrayBuffer-backed view. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported()) return null;
  const reg = await register();
  const sub = await reg.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}

export async function enablePush(memberId?: string | null): Promise<'ok' | 'denied' | 'unsupported'> {
  if (!pushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const { key, enabled } = await api.getPushKey();
  if (!enabled || !key) return 'unsupported';

  const reg = await register();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    }));

  await api.subscribePush(sub.toJSON(), memberId);
  return 'ok';
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await register();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.unsubscribePush(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}
